import { ISkillCharts, ISkillTotals, ISkillDemand } from 'types/reportsInterfaces'
import {
  IProject,
  ISkillOrIndustryOrRole,
  IEmployer,
  ISkillSuggestions,
  ISkillTranslate,
  ISkillDuplicates,
  IReviewQueue,
  INameEmbeddingStatus,
  IAdminAiSuggestions,
  IAdminAiSuggestionRun,
  IAdminAiSuggestionRunStatus,
  IAiTranslationRequest,
  IAiTranslationSuggestion,
  IAdminAiSuggestionAcceptResult,
} from 'types/cvsInterfaces'
import { ISkill, IIndustry, IRole } from 'types/levelItemInterfaces'
import { SubAPI } from './API'
import { updateDate } from '../utils/utils'

/**
 * Skill API.
 * @notExported
 */
class SkillAPI extends SubAPI {
  /**
   * Get all types of all skills.
   *
   * @returns Array of skills.
   */
  public getAll(controller?: AbortController): Promise<ISkillOrIndustryOrRole[]> {
    return this.api.get(`skills`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Get all types of all unpromoted skills.
   *
   * @returns Array of skills.
   */
  public getAllUnpromoted(controller?: AbortController): Promise<ISkillOrIndustryOrRole[]> {
    return this.api.get('skills/unpromoted', controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Get all skills.
   *
   * @returns Array of skills.
   */
  public getAllSkills(controller?: AbortController): Promise<ISkill[]> {
    return this.api.get(`skills/skills`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Get all unpromoted skills.
   *
   * @returns Array of skills.
   */
  public getAllUnpromotedSkills(controller?: AbortController): Promise<ISkill[]> {
    return this.api.get(`skills/unpromoted/skills`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Get all industries.
   *
   * @returns Array of industries.
   */
  public getAllIndustries(controller?: AbortController): Promise<IIndustry[]> {
    return this.api.get(`skills/industries`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Get all unpromoted industries.
   *
   * @returns Array of industries.
   */
  public getAllUnpromotedIndustries(controller?: AbortController): Promise<IIndustry[]> {
    return this.api.get(`skills/unpromoted/industries`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Get all roles.
   *
   * @returns Array of roles.
   */
  public getAllRoles(controller?: AbortController): Promise<IRole[]> {
    return this.api.get(`skills/roles`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Get all unpromoted roles.
   *
   * @returns Array of roles.
   */
  public getAllUnpromotedRoles(controller?: AbortController): Promise<IRole[]> {
    return this.api.get(`skills/unpromoted/roles`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Get skill, role and industry suggestions for everything selected so far: the ones that most often go
   * with them on other people's CVs, leaving out the given ones.
   *
   * @param skillIds - Skills, roles and industries selected so far.
   * @returns Suggested skills, roles and industries, the best first.
   */
  public getSkillSuggestions(skillIds: number[], controller?: AbortController): Promise<ISkillSuggestions> {
    return this.api.post(`skills/suggestions`, { skillIds }, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Rebuild the table the suggestions come from, e.g. after merging skills. It is also rebuilt nightly.
   *
   * @returns Number of skill pairs in the table.
   */
  public rebuildSuggestions(controller?: AbortController): Promise<{ pairs: number }> {
    return this.api.get(`skills/cooccurrences/rebuild`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Get all skill totals.
   *
   * @param body - Totals body.
   * @returns Array of skill totals data.
   */
  public getAllSkillTotals(
    body: { type: string; isPromoted: boolean },
    controller?: AbortController
  ): Promise<ISkillTotals[]> {
    return this.api.post(`reports/skills/totals`, body, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Get skill totals for a team.
   *
   * @param teamId - Team ID.
   * @param body - Totals body.
   * @returns Array of skill totals data for the team.
   */
  public getTeamSkillTotals(
    teamId: number,
    body: { type: string; isPromoted: boolean },
    controller?: AbortController
  ): Promise<ISkillTotals[]> {
    return this.api.post(
      `reports/skills/totals/teams/${teamId}`,
      body,
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Get skill totals for a unit.
   *
   * @param unitId - Unit ID.
   * @param body - Totals body.
   * @returns Array of skill totals data for the unit.
   */
  public getUnitSkillTotals(
    unitId: number,
    body: { type: string; isPromoted: boolean },
    controller?: AbortController
  ): Promise<ISkillTotals[]> {
    return this.api.post(
      `reports/skills/totals/units/${unitId}`,
      body,
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Get all skill charts data.
   *
   * @param body - Chart body.
   * @returns Array of skill charts data.
   */
  public getAllSkillCharts(
    body: { type: string; isPromoted: boolean },
    controller?: AbortController
  ): Promise<ISkillCharts[]> {
    return this.api.post(`reports/skills/charts`, body, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Get skill charts data for a team.
   *
   * @param teamId - Team ID.
   * @param body - Chart body.
   * @returns Array of skill charts data for the team.
   */
  public getTeamSkillCharts(
    teamId: number,
    body: { type: string; isPromoted: boolean },
    controller?: AbortController
  ): Promise<ISkillCharts[]> {
    return this.api.post(
      `reports/skills/charts/teams/${teamId}`,
      body,
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Get skill charts data for a unit.
   *
   * @param body - Chart body.
   * @returns Array of skill charts data.
   */
  public getUnitSkillCharts(
    unitId: number,
    body: { type: string; isPromoted: boolean },
    controller?: AbortController
  ): Promise<ISkillCharts[]> {
    return this.api.post(
      `reports/skills/charts/units/${unitId}`,
      body,
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Update skill.
   *
   * @param data - Skill data.
   * @returns Updated skill.
   */
  public putSkill(
    data: { skill: Pick<ISkill, 'id'>; translations: ISkill['translations'] },
    controller?: AbortController
  ): Promise<ISkill> {
    return this.api.put(`skills/${data.skill.id}`, data, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Create skill.
   *
   * @param body - Skill data.
   * @returns Created skill.
   */
  public createSkill(
    body: { name: string; language: string; kind?: string },
    controller?: AbortController
  ): Promise<ISkillOrIndustryOrRole> {
    return this.api.post(`skills`, body, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Delete skill.
   *
   * @param id - Skill ID.
   * @returns Promise of skill deletion.
   */
  public delete(id: number, controller?: AbortController): Promise<void> {
    return this.api.delete(`skills/${id}`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Merge skills.
   *
   * @param newSkillId - New skill ID.
   * @param oldSkills - Old skill IDs.
   * @returns Promise of skill merge.
   */
  public merge(newSkillId: number, oldSkills: number[], controller?: AbortController): Promise<void> {
    return this.api.put(
      `skills/merge`,
      { newSkillId, oldSkills },
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Merge several groups of skills in one transaction: all or nothing.
   *
   * @param merges - Groups to merge.
   * @returns Promise of the merges.
   */
  public mergeBulk(merges: { newSkillId: number; oldSkills: number[] }[], controller?: AbortController): Promise<void> {
    return this.api.put(`skills/merge/bulk`, { merges }, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Get possible duplicate skills and broken names.
   *
   * @returns Duplicate groups and broken names.
   */
  public getDuplicates(controller?: AbortController): Promise<ISkillDuplicates> {
    return this.api.get(`skills/duplicates`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Get how many option names are ready for the similar-names-by-meaning check (#2014).
   *
   * @returns Embedded and missing counts, and whether a refresh is running.
   */
  public getNameEmbeddingStatus(controller?: AbortController): Promise<INameEmbeddingStatus> {
    return this.api.get(`skills/name-embeddings`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Start processing every option name that isn't ready for the similar-names check yet (#2014).
   * Runs in the background; poll getNameEmbeddingStatus.
   *
   * @returns The status when it started.
   */
  public refreshNameEmbeddings(controller?: AbortController): Promise<INameEmbeddingStatus> {
    return this.api.post(`skills/name-embeddings/refresh`, {}, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Mark a group of skills, certificates or competences as not duplicates.
   *
   * @param skillIds - The group's IDs.
   * @param type - What the IDs are; industries and roles are skills.
   * @returns Promise of the dismissal.
   */
  public dismissDuplicates(
    skillIds: number[],
    type: 'skill' | 'certificate' | 'competence' = 'skill',
    controller?: AbortController
  ): Promise<void> {
    return this.api.put(
      `skills/duplicates/dismiss`,
      { skillIds, type },
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Get the pending AI suggestions for data-quality fixes (#2015).
   *
   * @returns Suggestions, the most confident and most used first, and the run status.
   */
  public getAiSuggestions(controller?: AbortController): Promise<IAdminAiSuggestions> {
    return this.api.get(`skills/ai-suggestions`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Start asking the LLM for fixes to unreviewed options (#2015), or for the missing names of options
   * with a name in only one language (#2016). Runs in the background; poll getAiSuggestions.
   *
   * @param run - Which kind of run.
   * @returns The run status when it started.
   */
  public generateAiSuggestions(
    run: IAdminAiSuggestionRun['run'] = 'fixes',
    controller?: AbortController
  ): Promise<IAdminAiSuggestionRunStatus> {
    return this.api.post(
      `skills/ai-suggestions/generate`,
      { run },
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Ask the LLM for the missing name of one option, for the translate dialog (#2016).
   *
   * @param request - Option type, kind and its one name.
   * @returns The suggested missing name, if any, and how sure the LLM is.
   */
  public suggestTranslation(
    request: IAiTranslationRequest,
    controller?: AbortController
  ): Promise<IAiTranslationSuggestion> {
    return this.api.post(
      `skills/ai-suggestions/translate-name`,
      request,
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Accept AI suggestions: each is applied with the existing merge, translate and edit logic (#2015).
   *
   * @param ids - Suggestion IDs.
   * @param edits - Names an admin edited on name suggestions before accepting (#2016).
   * @returns Accepted IDs, and the ones that could not be applied.
   */
  public acceptAiSuggestions(
    ids: number[],
    edits: { id: number; fiName?: string; enName?: string }[] = [],
    controller?: AbortController
  ): Promise<IAdminAiSuggestionAcceptResult> {
    return this.api.put(
      `skills/ai-suggestions/accept`,
      { ids, ...(edits.length > 0 ? { edits } : {}) },
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Reject an AI suggestion; the same fix isn't suggested again for the option (#2015).
   *
   * @param id - Suggestion ID.
   * @returns Promise of the rejection.
   */
  public rejectAiSuggestion(id: number, controller?: AbortController): Promise<void> {
    return this.api.put(
      `skills/ai-suggestions/${id}/reject`,
      undefined,
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Swap which option of a merge suggestion is kept (#2019).
   *
   * @param id - Suggestion ID.
   * @returns The id of the option that is now kept.
   */
  public swapAiSuggestion(id: number, controller?: AbortController): Promise<{ keepId: number }> {
    return this.api.put(
      `skills/ai-suggestions/${id}/swap`,
      undefined,
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Get the options no admin has reviewed yet (#2013).
   *
   * @returns Unreviewed options by type with their number of people, most used first.
   */
  public getReviewQueue(controller?: AbortController): Promise<IReviewQueue> {
    return this.api.get(`skills/review-queue`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Get the number of people using each option, for the People column in Admin Skills (#2013).
   *
   * @returns Used options by type with their number of people; options no one uses are left out.
   */
  public getPeopleCounts(controller?: AbortController): Promise<IReviewQueue> {
    return this.api.get(`skills/people-counts`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Mark options reviewed (#2013). Industries and roles are skills.
   *
   * @param type - Option type.
   * @param ids - Option IDs.
   * @returns Promise of the update.
   */
  public markReviewed(
    type: 'skill' | 'certificate' | 'competence',
    ids: number[],
    controller?: AbortController
  ): Promise<void> {
    return this.api.put(`skills/reviewed`, { type, ids }, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Translate skill.
   *
   * @param data - Skill translations data.
   * @returns Updated skill.
   */
  public translate(data: ISkillTranslate, controller?: AbortController): Promise<ISkillOrIndustryOrRole> {
    return this.api.put(`skills/translate`, data, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Split skills.
   *
   * @param oldSkillId - Old skill ID.
   * @param newSkillIds - New skill IDs.
   * @returns Promise of skill split.
   */
  public split(oldSkillId: number, newSkillIds: number[], controller?: AbortController): Promise<void> {
    return this.api.put(
      'skills/split',
      { oldSkillId, newSkillIds },
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Promote skill.
   *
   * @param id - Skill ID.
   * @returns Promise of skill promotion.
   */
  public promote(id: number, controller?: AbortController): Promise<ISkillOrIndustryOrRole> {
    return this.api.put(`skills/${id}/promote`, undefined, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Get projects by SkillId matching personSkill.
   *
   * @param id - Skill ID.
   */
  public async getSkillProjects(id: number, controller?: AbortController): Promise<IProject[]> {
    const data: IProject[] = await this.api.get(
      `cvs/personSkills/projects/${id}`,
      controller ? { signal: controller.signal } : undefined
    )
    data.forEach(updateDate)
    return data
  }

  /**
   * Get projects by SkillId matching industry.
   *
   * @param id - Skill ID.
   */
  public async getIndustryProjects(id: number, controller?: AbortController): Promise<IProject[]> {
    const data: IProject[] = await this.api.get(
      `cvs/industry/projects/${id}`,
      controller ? { signal: controller.signal } : undefined
    )
    data.forEach(updateDate)
    return data
  }

  /**
   * Get projects by SkillId matching role.
   *
   * @param id - Skill ID.
   */
  public async getRoleProjects(id: number, controller?: AbortController): Promise<IProject[]> {
    const data: IProject[] = await this.api.get(
      `cvs/role/projects/${id}`,
      controller ? { signal: controller.signal } : undefined
    )
    data.forEach(updateDate)
    return data
  }

  /**
   * Get employers by SkillId matching role.
   *
   * @param id - Skill ID.
   */
  public async getRoleEmployers(id: number, controller?: AbortController): Promise<IEmployer[]> {
    const data: IEmployer[] = await this.api.get(
      `cvs/role/employers/${id}`,
      controller ? { signal: controller.signal } : undefined
    )
    data.forEach(updateDate)
    return data
  }

  /**
   * Import skill trends.
   *
   * @param data - Trends data file.
   * @returns Promise of skill trends import.
   */
  public importTrends(data: FormData, controller?: AbortController): Promise<void> {
    return this.api.post(`skills/trend/import`, data, controller ? { signal: controller.signal } : undefined, {
      'Content-Type': 'multipart/form-data',
    })
  }

  /**
   * Download skill trends export.
   *
   * @returns Excel file of skill trends.
   */
  public downloadExport(controller?: AbortController): Promise<Blob> {
    return this.api.get(
      'skills/trend/export',
      controller
        ? { responseType: 'blob', signal: controller.signal }
        : {
            responseType: 'blob',
          }
    )
  }

  /**
   * Get demand statistics.
   *
   * @param selectedTeam - Selected team ID.
   * @param type - Skill type.
   * @param isPromoted - Include promoted?
   * @returns Skill demand statistics.
   */
  public getDemandStatistics(
    selectedTeam: number,
    type: string,
    isPromoted: boolean,
    controller?: AbortController
  ): Promise<ISkillDemand[]> {
    return this.api.post(
      'reports/skills/charts/demand',
      { selectedTeam, type, isPromoted },
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Get demand statistics.
   *
   * @param selectedTeam - Selected team ID.
   * @param type - Skill type.
   * @param isPromoted - Include promoted?
   * @returns Skill demand statistics.
   */
  public getUnitDemandStatistics(
    selectedUnit: number,
    type: string,
    isPromoted: boolean,
    controller?: AbortController
  ): Promise<ISkillDemand[]> {
    return this.api.post(
      'reports/skills/units/charts/demand',
      { selectedUnit, type, isPromoted },
      controller ? { signal: controller.signal } : undefined
    )
  }
}

export const skillAPI = new SkillAPI()
